import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ClipboardList, History, Gauge, Trash2, RotateCcw, Pencil } from 'lucide-react';
import { API, TYPE_LABEL, TypeIcon, fmtMeter, StatusPill } from './fleetShared';
import FleetPlanTab from './FleetPlanTab';
import FleetHistoryTab from './FleetHistoryTab';

function MeterEditor({ unit, onSaved }) {
  const kind = unit.meter?.kind;
  const [val, setVal] = useState('');
  const [open, setOpen] = useState(false);
  if (!kind) return null;
  const save = async () => {
    if (!val) return;
    try {
      await axios.post(`${API}/equipment/${unit.id}/meter`, kind === 'km' ? { km: val } : { hours: val });
      toast.success('Reading saved'); setOpen(false); setVal(''); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  return (
    <div className="flex items-center gap-2 text-sm">
      <Gauge className="w-4 h-4 text-slate-500" />
      <span className="text-slate-700" data-testid="fleet-meter-value">{fmtMeter(unit.meter)}</span>
      {unit.can_edit && !open && <button onClick={() => setOpen(true)} data-testid="fleet-meter-edit" className="text-xs text-blue-600 hover:underline">update</button>}
      {open && (
        <span className="flex items-center gap-1">
          <input type="number" value={val} onChange={e => setVal(e.target.value)} placeholder={kind === 'km' ? 'km' : 'hrs'} data-testid="fleet-meter-input"
            className="w-28 px-2 py-1 border border-slate-300 rounded text-sm text-slate-900" />
          <button onClick={save} data-testid="fleet-meter-save" className="px-2 py-1 bg-blue-600 text-white rounded text-xs">Save</button>
          <button onClick={() => setOpen(false)} className="px-2 py-1 text-slate-500 text-xs">Cancel</button>
        </span>
      )}
    </div>
  );
}

// Remove from the fleet (kept on file with history) / bring back
function RetireControls({ unit, onChanged, onClose }) {
  const [busy, setBusy] = useState(false);
  if (!unit.can_edit) return null;
  const retire = async () => {
    const reason = window.prompt(`Remove ${unit.equipment_name} from the fleet?\nIt stays on file with its service history and can be restored.\n\nReason (optional):`);
    if (reason === null) return;
    setBusy(true);
    try { await axios.delete(`${API}/equipment/${unit.id}`, { params: { reason } }); toast.success(`${unit.equipment_name} removed from the fleet`); onChanged(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not remove'); }
    setBusy(false);
  };
  const restore = async () => {
    setBusy(true);
    try { await axios.post(`${API}/equipment/${unit.id}/restore`); toast.success('Back in the fleet'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not restore'); }
    setBusy(false);
  };
  return unit.retired ? (
    <button onClick={restore} disabled={busy} data-testid="fleet-unit-restore" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-50"><RotateCcw className="w-3.5 h-3.5" /> Restore to fleet</button>
  ) : (
    <button onClick={retire} disabled={busy} data-testid="fleet-unit-retire" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-red-200 text-red-700 hover:bg-red-50 text-xs font-semibold disabled:opacity-50"><Trash2 className="w-3.5 h-3.5" /> Remove from fleet</button>
  );
}

// Manual units: edit the basics here. Sheet units: only how intervals are tracked (details live on the Equipment tab).
function UnitDetailsEditor({ unit, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ make: unit.make || '', model: unit.model || '', year: unit.year || '', license_plate: unit.license_plate || '', operator: unit.operator || '', meter_kind: unit.meter?.kind || 'none' });
  if (!unit.can_edit) return null;
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      await axios.put(`${API}/equipment/${unit.id}`, unit.manual ? f : { meter_kind: f.meter_kind });
      toast.success('Saved'); setOpen(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  if (!open) return <button onClick={() => setOpen(true)} data-testid="fleet-unit-edit" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> {unit.manual ? 'Edit details' : 'Tracking'}</button>;
  const I = 'px-2 py-1 border border-slate-300 rounded text-xs text-slate-900';
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="fleet-unit-edit-form">
      {unit.manual && <>
        <input value={f.make} onChange={e => set('make', e.target.value)} placeholder="Make" className={`${I} w-24`} />
        <input value={f.model} onChange={e => set('model', e.target.value)} placeholder="Model" className={`${I} w-24`} />
        <input value={f.year} onChange={e => set('year', e.target.value)} placeholder="Year" className={`${I} w-16`} />
        <input value={f.license_plate} onChange={e => set('license_plate', e.target.value)} placeholder="Plate" className={`${I} w-24`} />
        <input value={f.operator} onChange={e => set('operator', e.target.value)} placeholder="Operator" className={`${I} w-32`} />
      </>}
      <select value={f.meter_kind} onChange={e => set('meter_kind', e.target.value)} className={I} data-testid="fleet-unit-meter-kind">
        <option value="km">Tracks km</option><option value="hours">Tracks hours</option><option value="none">Time only</option>
      </select>
      <button onClick={save} data-testid="fleet-unit-edit-save" className="px-2 py-1 bg-blue-600 text-white rounded text-xs">Save</button>
      <button onClick={() => setOpen(false)} className="px-2 py-1 text-slate-500 text-xs">Cancel</button>
      {!unit.manual && <span className="text-[10px] text-slate-500">Make / plate / operator come from the Equipment sheet</span>}
    </div>
  );
}

export default function FleetUnitModal({ eid, meta, onClose, onChanged }) {
  const [unit, setUnit] = useState(null);
  const [tab, setTab] = useState('plan');

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/equipment/${eid}`); setUnit(r.data); }
    catch { toast.error('Failed to load unit'); onClose(); }
  }, [eid, onClose]);
  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); onChanged(); };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-3 md:p-6 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl xl:max-w-[1320px] 2xl:max-w-[1480px] my-4" onClick={e => e.stopPropagation()} data-testid="fleet-unit-modal">
        {!unit ? <div className="p-8 text-center text-slate-500">Loading…</div> : (
          <>
            <div className="flex items-start justify-between gap-3 p-4 border-b border-slate-200">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-slate-100 text-slate-700"><TypeIcon type={unit.equipment_type} className="w-6 h-6" /></div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900" data-testid="fleet-unit-title">{unit.equipment_name}</h2>
                  <p className="text-xs text-slate-500">
                    {TYPE_LABEL[(unit.equipment_type || '').toLowerCase()] || unit.equipment_type}
                    {unit.year ? ` · ${unit.year}` : ''}{unit.make ? ` · ${unit.make} ${unit.model || ''}` : ''}
                    {unit.license_plate ? ` · Plate ${unit.license_plate}` : ''}{unit.operator ? ` · ${unit.operator}` : ''}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <StatusPill status={unit.retired ? 'retired' : unit.overall} testid="fleet-unit-overall" />
                    {unit.manual && <span className="text-[10px] font-semibold text-slate-500 border border-slate-300 rounded px-1.5 py-0.5" title="Added in Ops — not from the Equipment sheet">added in Ops</span>}
                    <MeterEditor unit={unit} onSaved={changed} />
                    <UnitDetailsEditor key={unit.id + (unit.meter?.kind || '')} unit={unit} onSaved={changed} />
                    <RetireControls unit={unit} onChanged={onChanged} onClose={onClose} />
                  </div>
                  {unit.retired && <p className="text-xs text-slate-600 mt-1" data-testid="fleet-unit-retired-note">Removed from the fleet {unit.retired_at ? `on ${unit.retired_at.slice(0, 10)}` : ''}{unit.retired_by ? ` by ${unit.retired_by}` : ''}{unit.retired_reason ? ` — ${unit.retired_reason}` : ''}.</p>}
                </div>
              </div>
              <button onClick={onClose} data-testid="fleet-unit-close"><X className="w-5 h-5 text-slate-500 hover:text-slate-700" /></button>
            </div>

            <div className="flex gap-1 px-4 pt-3">
              {[['plan', 'Maintenance Plan', ClipboardList], ['history', `Service History (${unit.history.length})`, History]].map(([k, label, Icon]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`fleet-tab-${k}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-t-lg text-sm font-medium border-b-2 ${tab === k ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
            <div className="p-4">
              {tab === 'plan' && <FleetPlanTab unit={unit} meta={meta} onChanged={changed} onLogService={() => setTab('history')} />}
              {tab === 'history' && <FleetHistoryTab unit={unit} meta={meta} onChanged={changed} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
